import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { clientTestimonials } from '../mock';

export const Testimonials3D = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const x = useTransform(scrollYProgress, [0, 1], [-100, 100]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-5, 5]);

  return (
    <section ref={containerRef} className="bg-dark py-24 md:py-32 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-20 space-y-6"
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Client Results
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            Real Professionals.
            <br />
            <span className="text-gold">Documented Results.</span>
          </motion.h2>
        </motion.div>

        {/* 3D Card Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {clientTestimonials.map((testimonial, index) => (
            <Testimonial3DCard key={index} testimonial={testimonial} index={index} />
          ))}
        </div>

        {/* Trusted By Section */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <p className="font-body text-sm md:text-base text-[#e8e6df]/60 max-w-3xl mx-auto">
            Trusted by driven professionals across finance, real estate, sales, and operations.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const Testimonial3DCard = ({ testimonial, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const cardRef = useRef(null);
  const [rotateX, setRotateX] = React.useState(0);
  const [rotateY, setRotateY] = React.useState(0);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateXValue = ((y - centerY) / centerY) * -10;
    const rotateYValue = ((x - centerX) / centerX) * 10;
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 100, rotateX: -45 }}
      animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
      transition={{ 
        delay: index * 0.1,
        duration: 0.8,
        type: "spring",
        stiffness: 100
      }}
      style={{ perspective: '1500px' }}
    >
      <motion.div
        ref={cardRef}
        className="relative bg-panel p-8 border border-[#c6a04a]/20 h-full cursor-pointer group"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{
          rotateX,
          rotateY,
        }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        whileHover={{ 
          scale: 1.02,
          borderColor: 'rgba(198, 160, 74, 0.6)',
          boxShadow: '0 20px 60px rgba(198, 160, 74, 0.3)'
        }}
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Shine Effect */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at ${rotateY * 5 + 50}% ${rotateX * 5 + 50}%, rgba(198, 160, 74, 0.2), transparent 50%)`,
            transform: 'translateZ(1px)'
          }}
        />

        {/* Content */}
        <div style={{ transform: 'translateZ(50px)' }}>
          {/* Result Badge */}
          <motion.div 
            className="inline-block bg-[#c6a04a]/20 border border-[#c6a04a]/50 px-4 py-2 mb-6"
            whileHover={{ scale: 1.05, translateZ: 20 }}
          >
            <p className="font-display text-sm md:text-base uppercase tracking-wide text-gold">
              {testimonial.result}
            </p>
          </motion.div>

          {/* Quote */}
          <p className="font-body text-sm md:text-base text-[#e8e6df] leading-relaxed font-light mb-6 italic">
            "{testimonial.quote}"
          </p>

          {/* Author */}
          <div className="border-t border-gold/20 pt-4">
            <p className="font-display text-lg uppercase tracking-wide text-cream mb-1">
              {testimonial.name}
            </p>
            <p className="font-body text-xs text-gold-soft">
              {testimonial.title}
            </p>
          </div>
        </div>

        {/* 3D Corner Accent */}
        <div 
          className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-gold/30"
          style={{ transform: 'translateZ(25px)' }}
        />
        <div 
          className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-gold/30"
          style={{ transform: 'translateZ(25px)' }}
        />
      </motion.div>
    </motion.div>
  );
};
